import { useCallback } from 'react';

import { getGTMConfig } from '@/data/events/handlers/gtm';
import { useSettings } from '@/data/Settings';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { triggerGA4Event } from 'presentation/events/services/ga4EcommerceService';
import { GA4_BOT_CHANNEL_MOBILE, NOT_AVAILABLE } from './constants';
import type {
	SendBotEscalatedSessionEventProps,
	SendBotProductSearchEventProps,
	SendBotSearchResultEventProps,
	SendBotSessionTopicEventProps,
} from './types';
import {
	buildBotEscalatedSessionPayload,
	buildBotProductSearchPayload,
	buildBotSearchResultPayload,
	buildBotSessionTopicPayload,
} from './utils';

export const useTrackDibotGA4 = ({ storeId }: { storeId: string | null }) => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { isMobileApp } = useMobileApp();

	const resolvedStoreId = storeId ?? physicalStoreId ?? NOT_AVAILABLE;

	const sendProductSearchEvent = useCallback(
		({ sessionId, topic, product }: SendBotProductSearchEventProps) => {
			const { ga4 } = getGTMConfig(settings);
			if (!ga4) return;

			const payload = buildBotProductSearchPayload({
				sessionId: sessionId ?? NOT_AVAILABLE,
				storeId: resolvedStoreId,
				topic,
				product,
			});

			if (!isMobileApp) {
				triggerGA4Event(payload);
				return;
			}

			logEventWithValues(payload.event, { ...payload, channel: GA4_BOT_CHANNEL_MOBILE });
		},
		[settings, resolvedStoreId, isMobileApp]
	);

	const sendSearchResultEvent = useCallback(
		({ sessionId, products }: SendBotSearchResultEventProps) => {
			const { ga4 } = getGTMConfig(settings);
			if (!ga4) return;

			const payload = buildBotSearchResultPayload({
				sessionId: sessionId ?? NOT_AVAILABLE,
				storeId: resolvedStoreId,
				products,
			});

			if (!isMobileApp) {
				triggerGA4Event(payload);
				return;
			}

			logEventWithValues(payload.event, { ...payload, channel: GA4_BOT_CHANNEL_MOBILE });
		},
		[settings, resolvedStoreId, isMobileApp]
	);

	const sendSessionTopicEvent = useCallback(
		({ sessionId, topic, interactionWith, interactionDate }: SendBotSessionTopicEventProps) => {
			const { ga4 } = getGTMConfig(settings);
			if (!ga4) return;

			const payload = buildBotSessionTopicPayload({
				sessionId: sessionId ?? NOT_AVAILABLE,
				storeId: resolvedStoreId,
				topic,
				interactionWith,
				interactionDate,
			});

			if (!isMobileApp) {
				triggerGA4Event(payload);
				return;
			}

			logEventWithValues(payload.event, { ...payload, channel: GA4_BOT_CHANNEL_MOBILE });
		},
		[settings, resolvedStoreId, isMobileApp]
	);

	const sendEscalatedSessionEvent = useCallback(
		({
			sessionId,
			topic,
			userInput,
			botMessage,
			successfulTransfer,
		}: SendBotEscalatedSessionEventProps) => {
			const { ga4 } = getGTMConfig(settings);
			if (!ga4) return;

			const payload = buildBotEscalatedSessionPayload({
				sessionId: sessionId ?? NOT_AVAILABLE,
				storeId: resolvedStoreId,
				topic,
				userInput,
				botMessage,
				successfulTransfer,
			});

			if (!isMobileApp) {
				triggerGA4Event(payload);
				return;
			}

			logEventWithValues(payload.event, { ...payload, channel: GA4_BOT_CHANNEL_MOBILE });
		},
		[settings, resolvedStoreId, isMobileApp]
	);

	return {
		sendProductSearchEvent,
		sendSearchResultEvent,
		sendSessionTopicEvent,
		sendEscalatedSessionEvent,
	};
};
